import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@nimbitlabs/ui';
import { 
  Globe, 
  FileText, 
  Activity
} from 'lucide-react';

const endpointSchema = z.object({
  name: z.string().max(200, 'Name must be 200 characters or less').optional(),
  environment: z.string().min(1, 'Please select an environment'),
  type: z.enum(['service', 'version', 'health', 'swagger', 'metrics', 'logs', 'other']),
  url: z.string().url('Please enter a valid URL').max(2000, 'URL must be 2000 characters or less'),
});

type EndpointFormData = z.infer<typeof endpointSchema>;

// UI-level endpoint type (includes environment for display)
export interface ServiceEndpoint {
  id: string;
  name?: string;
  environment: string;
  type: 'service' | 'version' | 'health' | 'swagger' | 'metrics' | 'logs' | 'other';
  url: string;
}

interface AddEditEndpointModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  endpoint?: ServiceEndpoint | null;
  onSave: (endpoint: Omit<ServiceEndpoint, 'id'> & { id?: string }) => void;
}

const endpointTypes = [
  { value: 'service', label: 'Service', icon: Globe },
  { value: 'version', label: 'Version', icon: FileText },
  { value: 'health', label: 'Health Check', icon: Activity },
  { value: 'swagger', label: 'API Documentation', icon: FileText },
  { value: 'metrics', label: 'Metrics', icon: Activity },
  { value: 'logs', label: 'Logs', icon: FileText },
  { value: 'other', label: 'Other', icon: Globe }
] as const;

export const AddEditEndpointModal: React.FC<AddEditEndpointModalProps> = ({ 
  open, 
  onOpenChange, 
  endpoint,
  onSave 
}) => {
  const { environments } = useEnvironmentsStore();
  
  const form = useForm<EndpointFormData>({
    resolver: zodResolver(endpointSchema),
    defaultValues: {
      name: '',
      environment: '',
      type: 'service',
      url: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const isEditing = !!endpoint;

  useEffect(() => {
    if (endpoint && open) {
      form.reset({
        name: endpoint.name || '',
        environment: endpoint.environment,
        type: endpoint.type,
        url: endpoint.url,
      });
    } else if (open && !endpoint) {
      form.reset({
        name: '',
        environment: environments.length > 0 ? environments[0].name || '' : '',
        type: 'service',
        url: '',
      });
    }
  }, [endpoint, open, environments, form]);

  const onSubmit = (data: EndpointFormData) => {
    onSave({
      id: endpoint?.id,
      ...data,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Endpoint' : 'Add New Endpoint'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Update the endpoint information.' 
              : 'Add a new endpoint for this service.'
            }
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="environment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Environment *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select environment" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {environments.map((env) => (
                          <SelectItem key={env.id} value={env.name || ''}>
                            {env.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {endpointTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className="h-4 w-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="https://..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom Name (Optional)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Leave empty to use type name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Add'} Endpoint
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
